import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MessageFoldersService } from '@modules/messages/services/message-folders.service';
import { MessageListStateService } from '@modules/messages/services/message-list-state.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { MessageFolder, type MessageFolderFormGroupConfig } from '@modules/messages/types/message-folder';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-message-folder-form',
  templateUrl: './message-folder-form.component.html',
  styleUrls: ['./message-folder-form.component.less'],
  standalone: false,
  providers: [
    WorkspaceIdentityService,
    ...MessageListStateService.providers({
      stateKey: StateKey.messagesFFListState,
    }),
    provideDetailFooterSlot(),
  ],
})
export class MessageFolderFormComponent
  extends FullFormBaseComponent<StitchType.MESSAGE_FOLDER, MessageFolder, MessageFolderFormGroupConfig>
  implements OnInit
{
  @Input() folder = new MessageFolder();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(MailAppStateService, { appLabel: APP_CRUMBS.mail });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Mail app › Folders › this folder. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.folders,
      fallbackView: TREE_CRUMBS.folders,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  public changesKey = 'folder';
  tabs: Tab[] = [
    { title: 'Messages', value: 'folder' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'messageFolders';
  public parentSuggestions: AutocompleteFactory<MessageFolder>;
  public messagesFilters: MessagesFilters;

  protected override childItemDragType = DragDataTypes.message;
  protected override childItemContainerIdField = 'folderId';

  private readonly messageListStateService = inject(MessageListStateService, { self: true });
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.messageFolder,
      childItemType: DragDataTypes.message,
      childService: this.messagesService,
      containerIdField: 'folderId',
      appStateService: this.stateService,
      spaceId: this.spaceContextStore.contextId(),
      requestContext: this.requestContext,
    };
  }

  constructor(
    private foldersService: MessageFoldersService,
    private messagesService: MessagesService,
    private stateService: MailAppStateService,
    injector: Injector,
  ) {
    super(injector, foldersService);
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = this.foldersService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] });
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.messageListStateService.value(), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.messagesFilters = new MessagesFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
        this.messagesFilters.applyListState(state);
      });
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  /**
   * Helpers
   */

  get stitchItem(): MessageFolder {
    return this.folder;
  }

  protected shouldRefreshList(prev, current) {
    return MessageFolder.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): MessageFolder {
    return MessageFolder.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.folder.asFormGroup({
      foldersService: this.foldersService,
      maxAttachmentSize: this.maxUploadSize(),
    });
  }
}
